/* eslint-disable max-lines -- Pre-existing violation */
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Menu} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {useGetAuthorizationProfilesQuery, useGetAuthorizationsQuery} from '@dms/api/security';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {orchestrationRoutes, settingsRoutes} from '@dms/routes';
import {useIsLicenceEnabled, usePermissions, useTenant} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {useCodeLists} from '../../../hooks/useCodeLists';
import {NavigationItem} from '../../../types/NavigationItem';
import {getRentalCodelistLabel} from '../../../utils/getRentalCodelistLabel';

const HYUNDAI_AUTHORIZATION_NAME = 'Hyundai CZE';
const SHOULD_SHOW_CUSTOMER_CODE_DEFINITION = false;
/**
 * An ordinary Phrase key since T20-139103 (FE-7) folded the orchestration
 * module's private i18next namespace in. It used to be the namespace-prefixed
 * `orchestration:ruleList.title`, written out as a literal because this library
 * is `type:util` and may not import `@dms/orchestration` to read the namespace
 * constant — and it kept pointing at that key for two stages after the key was
 * deleted, rendering its own name in the settings sidebar. Nothing noticed:
 * this label lives in another library, and the spec asserting it held the same
 * literal, so the pair agreed with each other while agreeing with nothing else.
 * The spec now checks the key against the published catalogue as well.
 */
const ORCHESTRATION_RULES_LABEL_KEY = 'entity.automationRule.list.title';

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function SettingsNavigation() {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {tenant} = useTenant();
  const {data: codelists} = useCodeLists();

  const [
    canReadVehicleWarehouse,
    canReadEmployee,
    canReadPneuhotel,
    canReadCustomFieldsDefinitions,
    canReadArticle,
    canReadBusinessCase,
    canReadVehicle,
    canUpdateRole,
    canUpdateUser,
    warehouseDictionaryRead,
    canReadMandatoryFields,
    canReadTenantMultiFactorAuthenticationSettings,
    canReadRentalVehicle,
    canReadTeam,
    canReadFacebookLeads,
    canReadReporting,
    canReadAiLeads,
    canReadServiceAccount,
    canReadOAuthClient,
    canReadRecordAudit,
    canReadPreAccountingProcessing,
    canReadNegativeDocumentCancellationDefinition,
    canReadQrCode,
    canReadRoundingDefinition,
    canReadTaxDocumentForPaymentSettings,
    canReadCashRegisterDocument,
    canCreateCashRegisterDocument,
    canReadInvoice,
    canCreateInvoice,
    canReadVatCalculationDefinition,
    canReadVatCheckTotalsDefinition,
    canReadTelephonySettings,
    canReadMarginInvoiceSettings,
  ] = usePermissions({
    permissionKeys: [
      'vehicleWarehouseRead',
      'employeeRead',
      'pneuhotelRead',
      'readCustomFieldsDefinitions',
      'articleRead',
      'businessCaseRead',
      'vehicleRead',
      'updateRole',
      'updateUser',
      'warehouseDictionaryRead',
      'readMandatoryFields',
      'canReadTenantMultiFactorAuthenticationSettings',
      'readRentalVehicle',
      'teamRead',
      'readFacebookLeads',
      'reportingRead',
      'aiLeadSettingsRead',
      'readServiceAccount',
      'readOAuthClient',
      'recordAuditRead',
      'readPreAccountingProcessing',
      'readNegativeDocumentCancellationDefinition',
      'readQrCode',
      'readRoundingDefinition',
      'readTaxDocumentForPaymentSettings',
      'readCashRegisterDocument',
      'createCashRegisterDocument',
      'readInvoice',
      'createInvoice',
      'readVatCalculationDefinition',
      'readVatCheckTotalsDefinition',
      'telephonySettingsRead',
      'readMarginInvoiceSettings',
    ],
    scopes: {
      vehicleRead: {
        // there is no specific vehicle record in the settings context
        participation: 'SKIP',
        // there is no specific vehicle record in the settings context
        branchId: 'SKIP',
      },
    },
  });

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();
  const reportingAuthorizationQueryArg =
    canReadReporting && tenant?.id ? {'x-tenant': tenant.id} : skipToken;
  const {data: authorizations} = useGetAuthorizationsQuery(reportingAuthorizationQueryArg);
  const {data: authorizationProfiles} = useGetAuthorizationProfilesQuery(
    reportingAuthorizationQueryArg
  );
  const hyundaiAuthorizationId = authorizations?.find(
    (item) => item?.name === HYUNDAI_AUTHORIZATION_NAME
  )?.id;
  const hasActiveHyundaiAuthorizationProfile = authorizationProfiles?.some(
    (item) => item?.authorizationId === hyundaiAuthorizationId && item?.isActive
  );
  const isSalesModuleEnabled = canReadBusinessCase;
  const isRecordAuditVisible =
    canReadRecordAudit && isFeatureEnabled(featureFlags.CORE_CHECKLIST_AUDITS);
  const isMfaEnabled =
    isFeatureEnabled(featureFlags.SETTINGS_TWO_FACTOR_AUTH) &&
    canReadTenantMultiFactorAuthenticationSettings;
  const isEmployeeManagementVisible =
    canReadEmployee && isFeatureEnabled(featureFlags.EMPLOYEE_MANAGEMENT);

  const customerNavigationItems = buildArray<NavigationItem>([])
    .when(SHOULD_SHOW_CUSTOMER_CODE_DEFINITION && canReadVehicle, {
      id: 'customerCodeDefinition',
      label: i18n.t('entity.seriesDefinition.labels.customerCodeDefinition'),
      href: settingsRoutes.customerCodeDefinition,
    })
    .when(canReadVehicle && isWorkshopModuleEnabled, {
      id: 'bankTransferSettings',
      label: i18n.t('page.settings.bankTransfer.navigation'),
      href: settingsRoutes.bankTransferSettings,
    })
    .whenNotNil(codelists?.customer, (customerCodelists) =>
      customerCodelists.map((codeList) => ({
        id: codeList.codelistId,
        label: codeList.name,
        href: composePath(settingsRoutes.codelistDetail, {params: {id: codeList.codelistId}}),
      }))
    );

  const menuItems = buildArray<NavigationItem>([])
    .when(isSalesModuleEnabled, {
      id: 'sales',
      label: i18n.t('page.salesSettings.title'),
      leftIcon: 'action/assessment',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'advertising',
          label: i18n.t('page.salesSettings.labels.advertising'),
          href: settingsRoutes.advertising,
        })
        .add({
          id: 'businessCases',
          label: i18n.t('page.businessCases.title'),
          href: settingsRoutes.businessCase,
        })
        .add({
          id: 'interest',
          label: i18n.t('entity.interest.labels.interest'),
          href: settingsRoutes.interest,
        })
        .when(isFeatureEnabled(featureFlags.CAMPAIGNS), {
          id: 'campaigns',
          label: i18n.t('entity.salesCampaign.labels.title'),
          href: settingsRoutes.campaigns,
        })
        .add({
          id: 'productCatalogue',
          label: i18n.t('entity.productCatalog.labels.productCatalog'),
          href: settingsRoutes.productCatalog,
        })
        .add({
          id: 'codelists',
          label: i18n.t('page.codelists.pageTitle'),
          href: settingsRoutes.codelists,
        })
        .when(isFeatureEnabled(featureFlags.SALES_WARRANTY), {
          id: 'warranty',
          label: i18n.t('page.salesSettings.labels.warranty'),
          href: settingsRoutes.warranty,
        })
        .add({
          id: 'salesServiceOrderVariants',
          label: i18n.t('entity.order.labels.serviceOrderVariants'),
          href: settingsRoutes.salesServiceOrderVariants,
        })
        .when(canReadVehicleWarehouse && isFeatureEnabled(featureFlags.VEHICLE_WAREHOUSE), {
          id: 'vehicleWarehouseManagement',
          label: i18n.t('page.salesSettings.labels.vehicleWarehouseManagement'),
          href: settingsRoutes.vehicleWarehouseManagement,
        })
        .add({
          id: 'reservation',
          label: i18n.t('page.reservationSettings.labels.pageTitle'),
          href: settingsRoutes.reservation,
        })
        .when(
          isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT) &&
            !isFeatureEnabled(featureFlags.SALES_PURCHASE_PRICE),
          {
            id: 'financing',
            label: i18n.t('entity.financing.labels.pageTitle'),
            href: settingsRoutes.financing,
          }
        )
        .when(isFeatureEnabled(featureFlags.SALES_PURCHASE_PRICE), {
          id: 'purchasePrice',
          label: i18n.t('page.settings.purchasePrice.title'),
          href: settingsRoutes.purchasePrice,
        })
        .add({
          id: 'chatSettings',
          label: i18n.t('entity.chatSettings.labels.pageTitle'),
          href: settingsRoutes.chatSettings,
        })
        .when(isFeatureEnabled(featureFlags.AI_LEAD_MANAGEMENT) && canReadAiLeads, {
          id: 'aiLeadManagement',
          label: i18n.t('page.settings.labels.aiLeadManagement'),
          href: settingsRoutes.aiLeadManagement,
        })
        .when(
          canReadAiLeads && isSalesModuleEnabled && isFeatureEnabled(featureFlags.AI_LEAD_LOG),
          {
            id: 'aiLeadManagementHistory',
            label: i18n.t('page.settings.labels.aiLeadEmailHistory'),
            href: settingsRoutes.aiLeadManagementHistory,
          }
        )
        .when(isFeatureEnabled(featureFlags.ONLINE_OFFER_SETTINGS), {
          id: 'digitalDeal',
          label: i18n.t('page.settings.labels.digitalDeal'),
          href: settingsRoutes.digitalDeal,
        }),
    })
    .when(canReadVehicle, {
      id: 'vehicle',
      label: i18n.t('entity.vehicle.labels.vehicle'),
      leftIcon: 'maps/directions_car',
      items: [
        {
          id: 'topMakes',
          label: i18n.t('general.labels.topMakes'),
          href: settingsRoutes.vehicleTopMakes,
        },
        {
          id: 'vehicleCustomCatalogue',
          label: i18n.t('entity.customCatalogue.labels.title'),
          href: settingsRoutes.vehicleCustomCatalogue,
        },
        {
          id: 'vehicleSeriesDefinition',
          label: i18n.t('page.salesSettings.labels.vehicleSeriesDefinitionTitle'),
          href: settingsRoutes.vehicleSeriesDefinition,
        },
        {
          id: 'inspections',
          label: i18n.t('entity.vehicle.labels.inspections'),
          href: settingsRoutes.vehicleInspections,
        },
        {
          id: 'condition',
          label: i18n.t('page.vehicleSettings.labels.condition'),
          href: settingsRoutes.vehicleCondition,
        },
        {
          id: 'parking',
          label: i18n.t('page.vehicleSettings.labels.parking'),
          href: settingsRoutes.vehicleParking,
        },
        {
          id: 'warrantyTypes',
          label: i18n.t('page.vehicleSettings.labels.warrantyTypes'),
          href: settingsRoutes.warrantyTypes,
        },
      ],
    })
    .when(canReadRentalVehicle, {
      id: 'rentalVehicle',
      label: i18n.t('entity.rental.labels.rental'),
      leftIcon: 'automotive/rental',
      items: [
        {
          id: 'priceGroupList',
          label: i18n.t('entity.rental.labels.priceGroups'),
          href: settingsRoutes.priceGroupList,
        },
        {
          id: 'carRentalSeries',
          label: i18n.t('entity.rental.labels.carRentalSeries'),
          href: settingsRoutes.carRentalSeries,
        },
        ...(codelists?.rental?.map((codeList) => ({
          id: codeList.codelistId,
          label: getRentalCodelistLabel(codeList.codelistId, codeList.name),
          href: composePath(settingsRoutes.editRentalCodeList, {params: {id: codeList.codelistId}}),
        })) ?? []),
      ],
    })
    .when(customerNavigationItems.length > 0, {
      id: 'customer',
      label: i18n.t('entity.customer.labels.customer'),
      leftIcon: 'social/person',
      items: customerNavigationItems,
    })
    .add({
      id: 'userManagement',
      label: i18n.t('page.settings.labels.userManagement'),
      leftIcon: 'social/people',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'users',
          label: i18n.t('entity.user.labels.users'),
          href: settingsRoutes.userManagement,
          isDisabled: !canUpdateUser,
          leftIcon: canUpdateUser ? undefined : 'action/lock',
        })
        .add({
          id: 'roles',
          label: i18n.t('entity.person.labels.roles'),
          href: settingsRoutes.roleManagement,
          isDisabled: !canUpdateRole,
          leftIcon: canUpdateRole ? undefined : 'action/lock',
        })
        .when(canReadServiceAccount, {
          id: 'serviceAccounts',
          label: i18n.t('entity.serviceAccount.labels.serviceAccounts'),
          href: settingsRoutes.serviceAccountManagement,
        })
        .whenFeatureEnabled(featureFlags.OAUTH_CLIENTS_MANAGEMENT, {
          id: 'oauthClients',
          label: i18n.t('entity.oauthClient.labels.oauthClients'),
          href: settingsRoutes.oauthClientManagement,
          isDisabled: !canReadOAuthClient,
          leftIcon: canReadOAuthClient ? undefined : 'action/lock',
        })
        .when(isFeatureEnabled(featureFlags.CORE_TEAMS) && canReadTeam, {
          id: 'teamManagement',
          label: i18n.t('page.settings.labels.teamManagement'),
          href: settingsRoutes.teamManagement,
        }),
    })
    .add({
      id: 'templateManagement',
      label: i18n.t('page.settings.labels.templateManagement'),
      leftIcon: 'action/description',
      href: settingsRoutes.templateManagement,
    })
    .add({
      id: 'gdrpConsentTypes',
      label: i18n.t('entity.gdpr.labels.gdprConsentTypes'),
      leftIcon: 'content/ballot',
      href: settingsRoutes.gdprConsentTypes,
      hasSeparator: !isWorkshopModuleEnabled,
    })
    .when(isWorkshopModuleEnabled, {
      id: 'workshop',
      label: i18n.t('module.workshop.title'),
      leftIcon: 'action/build',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: [
            {
              id: 'workType',
              label: i18n.t('entity.order.labels.workType'),
              href: settingsRoutes.workType,
            },
            {
              id: 'vehicleGroup',
              label: i18n.t('entity.workshop.labels.vehicleGroups'),
              href: settingsRoutes.vehicleGroups,
            },
            {
              id: 'workshopUnits',
              label: i18n.t('entity.workshop.labels.units'),
              href: settingsRoutes.workshopUnits,
            },
            {
              id: 'workshopVehicleClasses',
              label: i18n.t('entity.workshop.labels.vehicleClasses'),
              href: settingsRoutes.workshopVehicleClasses,
            },
            {
              id: 'workshopInsuranceTypes',
              label: i18n.t('entity.workshop.labels.insuranceTypes'),
              href: settingsRoutes.workshopInsuranceTypes,
            },
          ],
        })
        .add({
          id: 'labourRates',
          label: i18n.t('entity.workshop.labels.labourRates'),
          href: settingsRoutes.labourRates,
        })
        .add({
          id: 'labourCatalogues',
          label: i18n.t('entity.workshop.labels.labourCatalogues'),
          href: settingsRoutes.labourCatalogues,
        })
        .whenFeatureEnabled(featureFlags.WORKSHOP_INTEREST, {
          id: 'workshopInterest',
          label: i18n.t('entity.interest.labels.interest'),
          href: settingsRoutes.workshopInterest,
        })
        .add({
          id: 'serviceCaseType',
          label: i18n.t('entity.workshopPresetsCategory.labels.serviceCaseType'),
          href: settingsRoutes.serviceCaseType,
        })
        .add({
          id: 'workshopServiceOrderVariants',
          label: i18n.t('entity.order.labels.serviceOrderVariants'),
          href: settingsRoutes.serviceOrderVariants,
        })
        .add({
          id: 'servicePackages',
          label: i18n.t('entity.workshopServicePackages.labels.title'),
          href: settingsRoutes.workshopServicePackages,
        })
        .add({
          id: 'workshopCustomerGroups',
          label: i18n.t('entity.rabatDiscounts.labels.rabatDiscounts'),
          href: settingsRoutes.workshopCustomerGroups,
        })
        .add({
          id: 'workshopSmallUnitMaterial',
          label: i18n.t('entity.workshopMinorUnitMaterial.labels.minorUnitMaterial'),
          href: settingsRoutes.workshopSmallUnitMaterial,
        })
        .add({
          id: 'customerContracts',
          label: i18n.t('entity.customerContract.labels.customerContracts'),
          href: settingsRoutes.customerContracts,
        })
        .add({
          id: 'workshopMeasurementUnits',
          label: i18n.t('page.settings.labels.measurementUnits'),
          href: settingsRoutes.workshopMeasurementUnits,
        })
        .add({
          id: 'workshopServiceOrderGroups',
          label: i18n.t('page.settings.labels.serviceOrderGroups'),
          href: settingsRoutes.workshopServiceOrderGroups,
        })
        .add({
          id: 'workshopSelectableMaterials',
          label: i18n.t('page.settings.labels.selectableMaterials'),
          href: settingsRoutes.workshopSelectableMaterials,
        }),
    })
    .when(canReadArticle && warehouseDictionaryRead, {
      id: 'warehouseManagement',
      label: i18n.t('page.warehouseSettings.labels.warehouseSettings'),
      leftIcon: 'custom/warehouse',
      hasSeparator: canReadPneuhotel && !isEmployeeManagementVisible,
      items: buildArray<NavigationItem>([])
        .when(warehouseDictionaryRead, {
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: buildArray<NavigationItem>([
            {
              id: 'suppliers',
              label: i18n.t('entity.warehouse.labels.suppliers'),
              href: settingsRoutes.suppliers,
            },
            {
              id: 'supplierAccounts',
              label: i18n.t('entity.warehouse.labels.supplierAccounts'),
              href: settingsRoutes.warehousesSupplierAccounts,
            },
            {
              id: 'supplierOrderTypes',
              label: i18n.t('entity.warehouse.labels.supplierOrderTypes'),
              href: settingsRoutes.warehousesSupplierOrderTypes,
            },
            {
              id: 'manufacturers',
              label: i18n.t('entity.warehouse.labels.manufacturers'),
              href: settingsRoutes.warehousesManufacturers,
            },
            {
              id: 'measurementUnits',
              label: i18n.t('entity.warehouse.labels.measurementUnits'),
              href: settingsRoutes.warehousesMeasurementUnits,
            },
            {
              id: 'dispensingUnitsPresets',
              label: i18n.t('entity.warehouse.labels.dispensingUnitsPresets'),
              href: settingsRoutes.warehousesDispensingUnitsPresets,
            },
            {
              id: 'marketingCode',
              label: i18n.t('entity.warehouse.labels.marketingCode'),
              href: settingsRoutes.warehousesMarketingCode,
            },
            {
              id: 'warehouses/article-category',
              label: i18n.t('entity.warehouse.labels.category'),
              href: settingsRoutes.warehousesArticleCategory,
            },
            {
              id: 'warehouses/supplier-discount-groups',
              label: i18n.t('entity.warehouse.labels.supplierDiscounts'),
              href: settingsRoutes.warehousesSupplierDiscountGroups,
            },
          ]),
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses',
          label: i18n.t('page.warehouses.labels.title'),
          href: settingsRoutes.warehousesList,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/supplierOrderVariants',
          label: i18n.t('page.warehouses.labels.supplierOrderVariants'),
          href: settingsRoutes.warehousesSupplierOrderVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/direct-sale-variants',
          label: i18n.t('entity.warehouse.labels.directSaleVariants'),
          href: settingsRoutes.warehousesDirectSaleVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/transfer-order-variants',
          label: i18n.t('entity.warehouse.labels.transferOrderVariants'),
          href: settingsRoutes.warehousesTransferOrderVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/spaces',
          label: i18n.t('entity.warehouse.labels.spaces'),
          href: settingsRoutes.warehousesSpaces,
        }),
    })
    .whenFeatureEnabled(featureFlags.AFTS_CHECKOUT_SETTINGS, {
      id: 'checkout',
      label: i18n.t('entity.businessCase.labels.checkout'),
      leftIcon: 'action/payment',
      items: [
        {
          id: 'checkoutProfiles',
          label: i18n.t('page.settings.labels.profiles'),
          href: settingsRoutes.checkoutProfiles,
        },
        {
          id: 'checkoutAssignmentRules',
          label: i18n.t('entity.automationRule.list.title'),
          href: settingsRoutes.checkoutAssignmentRules,
        },
      ],
    })
    .when(isEmployeeManagementVisible, {
      id: 'employee',
      label: i18n.t('module.employee.title'),
      leftIcon: 'custom/employee',
      hasSeparator: canReadPneuhotel,
      items: [
        {
          id: 'professions',
          label: i18n.t('entity.employeeProfessions.labels.title'),
          href: settingsRoutes.employeeProfessions,
        },
        {
          id: 'competences',
          label: i18n.t('entity.employeeCompetences.labels.title'),
          href: settingsRoutes.employeeCompetences,
        },
        {
          id: 'costCenters',
          label: i18n.t('entity.employeeCostCenters.labels.title'),
          href: settingsRoutes.employeeCostCenters,
        },
        {
          id: 'workTeams',
          label: i18n.t('entity.employeeWorkTeams.labels.title'),
          href: settingsRoutes.employeeWorkTeams,
        },
      ],
    })
    .when(canReadPneuhotel, {
      id: 'tiresInventory',
      label: i18n.t('module.tiresInventory.title'),
      leftIcon: 'modules/pneu_hotel',
      items: [
        {
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: [
            {
              id: 'tireCategory',
              label: i18n.t('entity.tiresInventory.labels.tireCategory'),
              href: settingsRoutes.tireCategory,
            },
            {
              id: 'tiresInventory-manufacturers',
              label: i18n.t('entity.tiresInventory.labels.tireManufacturers'),
              href: settingsRoutes.tireManufacturers,
            },
            {
              id: 'tireWidth',
              label: i18n.t('entity.tiresInventory.labels.tireWidth'),
              href: settingsRoutes.tireWidth,
            },
            {
              id: 'tireAspectRatio',
              label: i18n.t('entity.tiresInventory.labels.tireAspectRatio'),
              href: settingsRoutes.tireAspectRatio,
            },
            {
              id: 'tireRimSize',
              label: i18n.t('entity.tiresInventory.labels.tireRimSize'),
              href: settingsRoutes.tireRimSize,
            },
            {
              id: 'tireLoadIndex',
              label: i18n.t('entity.tiresInventory.labels.tireLoadIndex'),
              href: settingsRoutes.tireLoadIndex,
            },
            {
              id: 'tireSpeedRatio',
              label: i18n.t('entity.tiresInventory.labels.tireSpeedRatio'),
              href: settingsRoutes.tireSpeedRatio,
            },
            {
              id: 'tiresInventory-locations',
              label: i18n.t('entity.tiresInventory.labels.tireLocations'),
              href: settingsRoutes.tireLocations,
            },
            {
              id: 'tireCondition',
              label: i18n.t('entity.tiresInventory.labels.tireCondition'),
              href: settingsRoutes.tireCondition,
            },
          ],
        },
        {
          id: 'tiresInventory-general',
          label: i18n.t('general.labels.general'),
          href: settingsRoutes.tiresInventory,
        },
        {
          id: 'tireWarehouses',
          label: i18n.t('entity.tireWarehouses.labels.title'),
          href: settingsRoutes.tireWarehouses,
        },
        {
          id: 'tireTransit',
          label: i18n.t('entity.tireTransit.labels.title'),
          href: settingsRoutes.tireTransit,
        },
      ],
    })
    .when(canReadReporting && hasActiveHyundaiAuthorizationProfile, {
      id: 'reporting',
      label: i18n.t('page.settings.labels.reporting'),
      leftIcon: 'sidebar/analytics',
      hasSeparator: !isMfaEnabled,
      items: [
        {
          id: 'mobisIris',
          label: i18n.t('page.settings.labels.mobisIris'),
          items: [
            {
              id: 'partDepartment',
              label: i18n.t('entity.partDepartment.title'),
              href: settingsRoutes.partDepartment,
            },
          ],
        },
      ],
    })
    .when(isMfaEnabled, {
      id: 'twoFactorSettings',
      label: i18n.t('page.settings.labels.security'),
      hasSeparator: true,
      leftIcon: 'hardware/security',
      href: settingsRoutes.twoFactorSettings,
    })
    .add({
      id: 'tenantManagement',
      label: i18n.t('entity.checkout.labels.tenantManagement'),
      leftIcon: 'places/business_center',
      items: buildArray<NavigationItem>()
        .add({
          id: 'tenantProfile',
          label: i18n.t('page.settings.labels.tenantProfile'),
          leftIcon: 'places/business_center',
          href: settingsRoutes.tenant,
        })
        .add({
          id: 'payPerUseMetrics',
          label: i18n.t('page.homepage.payPerUse.title'),
          leftIcon: 'editor/table_chart',
          href: settingsRoutes.payPerUseMetrics,
        })
        .add({
          id: 'billingInformation',
          label: i18n.t('entity.checkout.labels.billingInformation'),
          leftIcon: 'action/credit_card',
          href: settingsRoutes.billingInformation,
        })
        .add({
          id: 'tenantLogos',
          label: i18n.t('page.settings.labels.tenantLogs'),
          leftIcon: 'image/photo',
          href: settingsRoutes.tenantLogos,
        })
        .add({
          id: 'holidays',
          label: i18n.t('page.settings.labels.holidays'),
          leftIcon: 'action/event',
          href: settingsRoutes.holidayCalendar,
        }),
    })
    .when(canReadTelephonySettings && isFeatureEnabled(featureFlags.CORE_TELEPHONY), {
      id: 'telephony',
      label: i18n.t('page.settings.telephony.title'),
      leftIcon: 'communication/call',
      items: [
        {
          id: 'telephonyLines',
          label: i18n.t('page.settings.telephony.linesTitle'),
          href: settingsRoutes.telephony,
        },
        {
          id: 'telephonyGroups',
          label: i18n.t('page.settings.telephony.groupsTitle'),
          href: settingsRoutes.telephonyGroups,
        },
      ],
    })
    .add({
      id: 'general',
      label: i18n.t('page.generalSettings.labels.title'),
      leftIcon: 'action/dns',
      items: buildArray<NavigationItem>()
        .add({
          id: 'seriesDefinition',
          label: i18n.t('page.generalSettings.labels.seriesDefinitionLink'),
          href: settingsRoutes.seriesDefinition,
        })
        .add({
          id: 'digital_signature',
          label: i18n.t('entity.settings.labels.signatures'),
          href: settingsRoutes.signatures,
        })
        .whenFeatureEnabled(featureFlags.MESSAGE_TEMPLATES, {
          id: 'messageTemplates',
          label: i18n.t('page.messageTemplates.labels.list'),
          href: settingsRoutes.messageTemplates,
        })
        .whenFeatureEnabled(featureFlags.SCHEDULED_MESSAGES, {
          id: 'scheduledMessages',
          label: i18n.t('page.settings.labels.scheduledMessages'),
          href: settingsRoutes.scheduledMessages,
        })
        .when(canReadCustomFieldsDefinitions, {
          id: 'customFields',
          label: i18n.t('page.generalSettings.labels.customFields'),
          href: settingsRoutes.customFields,
        })
        .add({
          id: 'notifications',
          label: i18n.t('page.settings.notifications.title'),
          href: settingsRoutes.notificationsOverview,
        })
        .whenFeatureEnabled(featureFlags.ORCHESTRATION_MODULE, {
          id: 'automationRules',
          label: i18n.t(ORCHESTRATION_RULES_LABEL_KEY),
          href: orchestrationRoutes.overview,
        })
        .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
          id: 'generalChecklist',
          label: i18n.t('page.settings.generalChecklist.title'),
          href: settingsRoutes.generalChecklist,
        })
        .when(isRecordAuditVisible, {
          id: 'recordAuditDefinition',
          label: i18n.t('page.settings.recordAuditDefinition.title'),
          href: settingsRoutes.recordAuditDefinition,
        })
        .when(isRecordAuditVisible, {
          id: 'recordAuditSeriesDefinition',
          label: i18n.t('page.settings.recordAuditSeriesDefinition.title'),
          href: settingsRoutes.recordAuditSeriesDefinition,
        })
        .when(canReadMandatoryFields, {
          id: 'mandatoryFields',
          label: i18n.t('entity.mandatoryFields.title'),
          href: settingsRoutes.mandatoryFields,
        }),
    })
    .add({
      id: 'branches',
      label: i18n.t('entity.branch.labels.branches'),
      leftIcon: 'social/domain',
      hasSeparator: false,
      href: settingsRoutes.branches,
    })
    .when(canReadInvoice || canReadCashRegisterDocument, {
      id: 'accountingDocuments',
      label: i18n.t('page.accounting.labels.accounting'),
      leftIcon: 'editor/insert_drive_file',
      hasSeparator: true,
      items: buildArray<NavigationItem>([])
        .when(canReadCashRegisterDocument, {
          id: 'cashRegisters',
          label: i18n.t('page.accounting.labels.cashRegisters'),
          href: settingsRoutes.cashRegisters,
        })
        .when(canCreateCashRegisterDocument, {
          id: 'cashOperationsLimits',
          label: i18n.t('page.accounting.labels.cashRegisterLimitsTitle'),
          href: settingsRoutes.cashLimits,
        })
        .when(canCreateInvoice, {
          id: 'invoiceDefinition',
          label: i18n.t('page.accounting.labels.invoiceDefinition'),
          href: settingsRoutes.invoiceDefinition,
        })
        .when(canReadRoundingDefinition, {
          id: 'documentRoundingDefinitions',
          label: i18n.t('page.accounting.labels.documentRoundingDefinitions'),
          href: settingsRoutes.documentRoundingDefinitions,
        })
        .when(canReadInvoice && canReadNegativeDocumentCancellationDefinition, {
          id: 'invoiceCanceling',
          label: i18n.t('page.accounting.labels.invoiceCanceling'),
          href: settingsRoutes.invoiceCanceling,
        })
        .when(canReadMarginInvoiceSettings, {
          id: 'invoiceMargin',
          label: i18n.t('page.accountingSettings.labels.invoiceMargin'),
          href: settingsRoutes.invoiceMarginOverview,
        })
        .when(canReadInvoice, {
          id: 'proforma',
          label: i18n.t('page.accountingSettings.labels.proforma'),
          items: [
            {
              id: 'proformaCreation',
              label: i18n.t('page.accountingSettings.labels.proformaCreation'),
              href: settingsRoutes.proformaCreationOverview,
            },
            {
              id: 'proformaGeneration',
              label: i18n.t('page.accountingSettings.labels.proformaGeneration'),
              href: settingsRoutes.proformaGeneration,
            },
          ],
        })
        .when(canReadInvoice, {
          id: 'correctiveTaxDocuments',
          label: i18n.t('page.accountingSettings.labels.correctiveTaxDocumentsSettings'),
          href: settingsRoutes.correctiveTaxDocuments,
        })
        .when(canReadVatCalculationDefinition && canReadVatCheckTotalsDefinition, {
          id: 'vatTotalAndCalculation',
          label: i18n.t('page.accountingSettings.labels.vatTotalAndCalculation'),
          href: settingsRoutes.vatTotalAndCalculationOverview,
        })
        .when(canReadTaxDocumentForPaymentSettings, {
          id: 'taxDocumentForPayment',
          label: i18n.t('entity.accounting.labels.taxDocumentForPayment'),
          items: [
            {
              id: 'taxDocumentForPayment-issuedType',
              label: i18n.t('page.accountingSettings.labels.issuedType'),
              href: settingsRoutes.taxDocumentIssuedType,
            },
            {
              id: 'taxDocumentForPayment-importType',
              label: i18n.t('page.accountingSettings.labels.importType'),
              href: settingsRoutes.taxDocumentImportType,
            },
          ],
        })
        .when(canReadQrCode, {
          id: 'qrCodeSettings',
          label: i18n.t('page.accounting.labels.qrCodeSettingsTitle'),
          href: settingsRoutes.qrCodeSettings,
        })
        .when(isWorkshopModuleEnabled, {
          id: 'discountApplicationMethod',
          label: i18n.t('page.accountingSettings.labels.discountApplicationMethod'),
          href: settingsRoutes.discountApplicationMethod,
        })
        .when(canReadPreAccountingProcessing, {
          id: 'preAccounting',
          label: i18n.t('page.accountingSettings.labels.preAccountingProcessing'),
          items: [
            {
              id: 'accountingCodes',
              label: i18n.t('page.accountingSettings.labels.accountingCodes'),
              href: settingsRoutes.accountingCodes,
            },
            {
              id: 'invoiceCostGeneration',
              label: i18n.t('page.accountingSettings.labels.invoiceCostGeneration'),
              href: settingsRoutes.invoiceCostOverview,
            },
            {
              id: 'accountingOperationDefaultsOverview',
              label: i18n.t('page.accounting.labels.operationDefaults.title'),
              href: settingsRoutes.accountingOperationDefaultsOverview,
            },
            {
              id: 'operationAuthorizations',
              label: i18n.t('page.accounting.labels.operationAuthorizationsOverview'),
              href: settingsRoutes.accountingOperationAuthorizationsOverview,
            },
            {
              id: 'preProcessingCalculation',
              label: i18n.t('page.accounting.labels.preProcessingCalculation'),
              href: settingsRoutes.preAccountingProcessingOverview,
            },
            {
              id: 'accountingErrorLog',
              label: i18n.t('page.accounting.labels.accountingErrorLog'),
              href: settingsRoutes.accountingErrorLog,
            },
            {
              id: 'accountingMatrixManagement',
              label: i18n.t('page.accounting.labels.accountingMatrixManagement'),
              href: settingsRoutes.accountingMatrixManagement,
            },
            {
              id: 'accountingActivityLog',
              label: i18n.t('page.accounting.labels.accountingActivityLog'),
              href: settingsRoutes.accountingActivityLog,
            },
            {
              id: 'accountingIntegrations',
              label: i18n.t('page.accountingSettings.labels.integrations'),
              href: settingsRoutes.accountingIntegrations,
            },
          ],
        }),
    })
    .add({
      id: 'integratedServices',
      label: i18n.t('page.integrations.labels.title'),
      leftIcon: 'action/extension',
      items: buildArray<NavigationItem>([
        {
          id: 'vehicleServices',
          label: i18n.t('page.integrations.labels.vehicleServices'),
          href: settingsRoutes.vehicleServices,
        },
      ])
        .whenFeatureEnabled(featureFlags.KONZULTA_SMS, {
          id: 'smsGateways',
          label: i18n.t('page.integrations.labels.smsGateways'),
          href: settingsRoutes.smsGateways,
        })
        .add({
          id: 'emailServices',
          label: i18n.t('page.integrations.labels.emailServices'),
          href: settingsRoutes.emailServices,
        })
        .add({
          id: 'customerVerificationSerivces',
          label: i18n.t('page.integrations.labels.customerVerification'),
          href: settingsRoutes.customerVerificationServices,
        })
        .whenFeatureEnabled(featureFlags.WHATSAPP_INTEGRATION, {
          id: 'whatsApp',
          label: i18n.t('page.integrations.labels.whatsApp'),
          href: settingsRoutes.whatsApp,
        })
        .when(isFeatureEnabled(featureFlags.SETTINGS_FACEBOOK) && canReadFacebookLeads, {
          id: 'facebookCredentials',
          label: i18n.t('page.integrations.labels.facebookCredentials'),
          href: settingsRoutes.facebookCredentials,
        })
        .add({
          id: 'esigno',
          label: i18n.t('page.settings.labels.digitalSignature'),
          href: settingsRoutes.esignoDetail,
        })
        .when(
          isFeatureEnabled(featureFlags.AUTOSTACJA_CUSTOMER_WIDGET) ||
            isFeatureEnabled(featureFlags.SETTINGS_JBR),
          {
            id: 'externalSystems',
            label: i18n.t('page.integrations.labels.externalSystems'),
            items: buildArray<NavigationItem>()
              .whenFeatureEnabled(featureFlags.AUTOSTACJA_CUSTOMER_WIDGET, {
                id: 'autostacjaWidget',
                label: i18n.t('page.integrations.labels.autostacja'),
                href: settingsRoutes.autostacjaSettings,
              })
              .whenFeatureEnabled(featureFlags.SETTINGS_JBR, {
                id: 'jbr',
                label: i18n.t('page.integrations.labels.jbr'),
                href: settingsRoutes.jbr,
              }),
          }
        ),
    })
    .add({
      id: 'localization',
      label: i18n.t('general.localeConfig.localization'),
      leftIcon: 'action/language',
      href: settingsRoutes.locale,
    });

  const allItems = getMenuItems(menuItems);
  // Prefer the most specific href so a nested page (e.g. /telephony/groups) does not light up its parent (/telephony).
  const activeItem = allItems
    .filter((item) => item.href && pathname.includes(item.href))
    .toSorted((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0];

  const handleItemChange = (id: string) => {
    const item = allItems.find((item) => item.id === id);
    if (item?.href) {
      navigate(item.href);
    }
  };

  return (
    <Menu
      items={menuItems}
      activeItemId={activeItem?.id}
      onItemChange={handleItemChange}
      isSearchable
      data-testid="menu"
    />
  );
}

const getMenuItems = (items: NavigationItem[]): NavigationItem[] =>
  items.flatMap((item) => (item.items ? getMenuItems(item.items) : item));
